import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, MoreVertical, Gauge, X, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Filter, Box, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { swalTheme } from '../lib/swalTheme';
import { usePermissions } from './usePermissions'; // افترضت إن عندك هوك الصلاحيات
import { useFavoriteFilters } from './useFavoriteFilters';
import { useFontSize } from './FontSizeContext';

const MeterList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { canCreate } = usePermissions('meters'); // التأكد من صلاحية الإنشاء

  // State
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // URL Parameters
  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 50;
  const q = searchParams.get('q') || '';
  const meterType = searchParams.get('meter_type') || '';

  const { saveFavorite } = useFavoriteFilters('METER');
  const handleSaveFavoriteFilter = async () => {
    const { value: name } = await Swal.fire({
      ...swalTheme(),
      title: t('favorites.namePrompt', 'Name this favorite filter'),
      input: 'text',
      inputPlaceholder: t('favorites.namePlaceholder', 'e.g. My Open WOs'),
      showCancelButton: true,
      confirmButtonText: t('common.save', 'Save'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      inputValidator: (value) => !value?.trim() && t('favorites.nameRequired', 'Please enter a name')
    });
    if (name && name.trim()) {
      saveFavorite(name, { q, meter_type: meterType });
    }
  };

  // 1. Fetch Data
  const fetchMeters = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/meters', {
        params: { page, limit, q, meter_type: meterType },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMeters(response.data.data);
      setTotalCount(response.data.total);
    } catch (err) {
      console.error(err);
      toast.error(t('common.load_error', 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  }, [page, q, meterType, t]);

  useEffect(() => {
    const timer = setTimeout(() => fetchMeters(), 100);
    return () => clearTimeout(timer);
  }, [fetchMeters]);

  // 2. Update Filters
  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  // 🎯 تطبيق فلتر مفضّل جاي من السايدبار - مفيش state اسمه filters هنا،
  // كله لايف جوه الـ URL، فبنعمل merge على searchParams الحالية
  useEffect(() => {
    const applyFavorite = (e) => {
      if (e.detail?.appName === 'METER' && e.detail?.filters) {
        const favFilters = e.detail.filters;
        setSearchParams(prev => {
          const updated = Object.fromEntries(prev.entries());
          Object.entries(favFilters).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') updated[k] = v;
            else delete updated[k];
          });
          updated.page = '1';
          return updated;
        });
      }
    };
    window.addEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
    return () => window.removeEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
  }, [setSearchParams]);

  // 3. Navigation with Context
  const handleRowClick = (meterNum) => {
    const query = searchParams.toString();
    // 🎯 بنمرر الفلاتر كلها في الرابط عشان شاشة التفاصيل تقرأها للنافيجيشن
    navigate(`/meters/${meterNum}${query ? '?' + query : ''}`);
  };

  const totalPages = Math.ceil(totalCount / limit) || 1;

  return (
    <div className={`flex flex-col h-full bg-surface-app text-[0.85em] uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo' : 'font-sans'}`} dir={isRTL ? 'rtl' : 'ltr'} style={{ fontSize: `${fontSize}px` }}>

      {/* --- Header Section --- */}
      <div className="bg-surface-card p-3 border-b border-line shadow-sm z-20">
        <div className="flex justify-between items-center px-4">
          <div className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Gauge size={22} className="text-primary" />
              <h2 className="text-primary font-black text-lg italic uppercase tracking-widest">{t('meters.list.title', 'Meter Definitions')}</h2>
            </div>
          </div>

          <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button onClick={() => setSearchParams({})} className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors">
                <X size={16}/> {t('common.clear_filters', 'CLEAR FILTERS')}
            </button>
            <button onClick={handleSaveFavoriteFilter} className="flex items-center gap-2 px-4 py-2 text-status-warning hover:opacity-70 font-bold transition-colors">
                <Star size={14} /> {t('favorites.save', 'SAVE AS FAVORITE')}
            </button>
            {canCreate && (
              <button
                onClick={() => navigate('/meters/new')}
                className="bg-primary text-white px-8 py-2 rounded-sm font-black flex items-center gap-2 hover:bg-primary-hover shadow-lg transition-all"
              >
                <Plus size={16} /> {t('meters.list.new_meter', 'NEW METER')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* --- Table & Filter Section --- */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card border rounded-sm shadow-sm overflow-hidden">
          <table className={`w-full text-left border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className="p-3 border-x border-white/10">{t('meters.fields.meter_num', 'Meter Num')}</th>
                <th className="p-3 border-x border-white/10">{t('meters.fields.meter_name', 'Meter Name')}</th>
                <th className="p-3 border-x border-white/10">{t('meters.fields.type', 'Type')}</th>
                <th className="p-3 border-x border-white/10">{t('meters.fields.uom', 'UOM')}</th>
                <th className="p-3 text-center border-x border-white/10"></th>
              </tr>
              {/* Filter Row */}
              <tr className="bg-surface-panel">
                <td colSpan="2" className="p-1 border-x border-line">
                  <div className={`relative flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Search className={`absolute text-ink-secondary ${isRTL ? 'right-2' : 'left-2'}`} size={14} />
                    <input
                      className={`w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card ${isRTL ? 'pr-8' : 'pl-8'}`}
                      placeholder={t('meters.list.search', 'Filter by Num or Name...')}
                      value={q}
                      onChange={e => updateFilters({ q: e.target.value, page: 1 })}
                    />
                  </div>
                </td>
                <td className="p-1 border-x border-line">
                  <select
                    className="w-full p-1.5 text-primary font-black outline-none border border-line-subtle bg-surface-card"
                    value={meterType}
                    onChange={e => updateFilters({ meter_type: e.target.value, page: 1 })}
                  >
                    <option value="">{t('common.all', 'ALL')}</option>
                    <option value="CONTINUOUS">CONTINUOUS</option>
                    <option value="GAUGE">GAUGE</option>
                    <option value="CHARACTERISTIC">CHARACTERISTIC</option>
                  </select>
                </td>
                <td className="p-1 border-x border-line bg-surface-panel/30"></td>
                <td className="p-1 border-x border-line bg-surface-panel/30"></td>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle font-bold">
              {loading ? (
                 <tr><td colSpan="5" className="p-20 text-center font-black text-ink-secondary animate-pulse tracking-[0.2em]">{t('common.syncing', 'Syncing Data...')}</td></tr>
              ) : meters.length > 0 ? (
                meters.map((meter) => (
                  <tr key={meter.meter_id} className="border-b border-line-subtle hover:bg-primary-soft transition-colors cursor-pointer group" onClick={() => handleRowClick(meter.meter_num)}>
                    <td className="p-3 border-x border-line-subtle font-black text-primary group-hover:underline">{meter.meter_num}</td>
                    <td className="p-3 border-x border-line-subtle text-ink">{meter.meter_name}</td>
                    <td className="p-3 border-x border-line-subtle">
                      <span className={`px-2 py-0.5 rounded text-[0.69em] font-black uppercase ${
                        meter.meter_type === 'CONTINUOUS' ? 'bg-primary-soft text-primary'
                        : meter.meter_type === 'GAUGE' ? 'bg-status-warning-bg text-status-warning'
                        : 'bg-purple-100 text-purple-700'
                      }`}>
                        {meter.meter_type}
                      </span>
                    </td>
                    <td className="p-3 border-x border-line-subtle text-ink-secondary font-mono text-xs">{meter.unit_of_measure || '---'}</td>
                    <td className="p-3 text-center border-x border-white">
                      <MoreVertical size={16} className="mx-auto text-ink-secondary group-hover:text-ink transition-colors" />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="p-20 text-center text-ink-secondary italic uppercase font-black tracking-widest">
                    {t('meters.list.no_records', 'No Meters Found')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Pagination Footer --- */}
      <div className={`bg-primary-dark text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black text-[0.77em] ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Box size={14} className="text-primary"/>
                  <span>{t('common.showing', 'Showing')}: <span className="text-primary">{(page-1)*limit+1}-{Math.min(page*limit, totalCount)}</span></span>
              </div>
              <div className={`flex items-center gap-2 border-white/20 ${isRTL ? 'border-r pr-6 flex-row-reverse' : 'border-l pl-6'}`}>
                  <Filter size={14} className="text-status-warning"/>
                  <span>{t('common.total', 'Total')}: <span className="text-status-warning">{totalCount}</span></span>
              </div>
          </div>

          <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-status-warning disabled:opacity-20">
                      <ChevronsLeft size={18} className={isRTL ? 'rotate-180' : ''}/>
                  </button>
                  <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-status-warning disabled:opacity-20">
                      <ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''}/>
                  </button>
                  <div className="px-4 text-[0.85em] font-mono border-x border-white/10 mx-1">
                      <span className="text-status-warning text-sm">{page}</span> / {totalPages}
                  </div>
                  <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-status-warning disabled:opacity-20">
                      <ChevronRight size={18} className={isRTL ? 'rotate-180' : ''}/>
                  </button>
                  <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-status-warning disabled:opacity-20">
                      <ChevronsRight size={18} className={isRTL ? 'rotate-180' : ''}/>
                  </button>
              </div>
          </div>
      </div>

    </div>
  );
};

export default MeterList;
